import React, { useEffect, useState } from "react";
import Modal from "antd/es/modal/Modal";
import { listGrupos } from "../services/gruposService";
import { Select } from "antd";

const TIPO_NOTIFICACION = {
  NINGUNA: "Ninguna",
  ALERTA: "Alerta",
  INFORMACION: "Información",
  EVENTO: "Evento",
  CONVOCATORIA: "Convocatoria",
};


export default function ModalNotificaciones({
  createOpen,
  setCreateOpen,
  formData: parentFormData,
  setFormData: setParentFormData,
  handleCreateNotification,
}) {
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [localForm, setLocalForm] = useState({
    tipo: "",
    titulo: "",
    cuerpo: "",
    grupo_id: [],
    programada_en: "",
  });

  // Load grupos only when modal opens
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const data = await listGrupos();
        if (mounted) setGrupos(data);
      } catch (error) {
        console.error(error);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (createOpen) {
      // initialize local form from parent (if provided)
      setLocalForm((prev) => ({ ...prev, ...parentFormData }));
      load();
    }

    return () => {
      mounted = false;
    };
  }, [createOpen, parentFormData]);

  const onSubmit = (e) => {
    e.preventDefault();
    // validar que haya al menos un grupo seleccionado
    if (!localForm.grupo_id || !Array.isArray(localForm.grupo_id) || localForm.grupo_id.length === 0) {
      alert("Selecciona al menos un grupo");
      return;
    }
    // If parent expects local updates, call it
    if (setParentFormData) setParentFormData({ ...localForm });
    // Call parent's handler with the local form data
    if (handleCreateNotification) handleCreateNotification(localForm);
    // Close modal after submission (parent may also close it)
    setCreateOpen(false);
  };

  return (
    <Modal
      open={createOpen}
      onCancel={() => setCreateOpen(false)}
      title="Crear nueva notificación"
      footer={null}
    >
      {loading ? (
        <div className="space-y-3">
          <div className="h-4 w-32 bg-slate-200 rounded animate-pulse" />
          <div className="h-10 w-full bg-slate-100 rounded-xl animate-pulse" />
          <div className="h-4 w-24 bg-slate-200 rounded animate-pulse" />
          <div className="h-20 w-full bg-slate-100 rounded-xl animate-pulse" />
          <div className="flex items-center justify-end gap-2 pt-2">
            <div className="h-9 w-20 bg-slate-100 rounded-xl animate-pulse" />
            <div className="h-9 w-32 bg-slate-200 rounded-xl animate-pulse" />
          </div>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          {/* Tipo */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Tipo <span className="text-rose-500">*</span>
            </label>
            <select
              value={localForm.tipo}
              onChange={(e) => setLocalForm((f) => ({ ...f, tipo: e.target.value }))}
              required
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100"
            >
              {Object.entries(TIPO_NOTIFICACION).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {/* Título */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Título <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              value={localForm.titulo}
              onChange={(e) => setLocalForm((f) => ({ ...f, titulo: e.target.value }))}
              placeholder="Ej: Nueva actualización disponible"
              required
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100"
            />
          </div>

          {/* Cuerpo */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Cuerpo <span className="text-rose-500">*</span>
            </label>
            <textarea
              value={localForm.cuerpo}
              onChange={(e) => setLocalForm((f) => ({ ...f, cuerpo: e.target.value }))}
              placeholder="Contenido de la notificación..."
              required
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 min-h-[100px]"
            />
          </div>

          {/* Grupo */}
          <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Grupo <span className="text-rose-500">*</span>
              </label>
              <Select
                showSearch
                mode="multiple"
                placeholder="Selecciona uno o más grupos"
                optionFilterProp="children"
                value={localForm.grupo_id || []}
                onChange={(value) => setLocalForm((f) => ({ ...f, grupo_id: value }))}
                className="w-full"
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={grupos.map((g) => ({
                  label: g.nombre,
                  value: g.id,
                }))}
              />
            </div>
          </div>

          {/* Fecha programada */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Programar envío
            </label>
            <input
              type="date"
              value={localForm.programada_en || ""}
              onChange={(e) => setLocalForm((f) => ({ ...f, programada_en: e.target.value }))}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
            />
          </div>

          {/* Botones */}
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={() => {
                setCreateOpen(false);
              }}
              className="flex-1 rounded-xl border border-slate-300 text-slate-700 px-4 py-2 text-sm hover:bg-slate-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 rounded-xl bg-blue-600 text-white px-4 py-2 text-sm hover:bg-blue-500"
            >
              Crear notificación
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
